import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { ArrowUpRight, ArrowDownRight, Receipt, AlertTriangle } from 'lucide-react';
import { get, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface LedgerEntry {
  date: string;
  kind: 'bill' | 'payment';
  description: string;
  debit_inr: number;
  credit_inr: number;
  balance_inr: number;
  ref_id: string;
  receipt_number?: string;
}

interface LedgerResp {
  resident: {
    id: string;
    full_name: string;
    phone: string;
    unit: {
      id: string;
      number: string;
      block: { id: string; name: string } | null;
    } | null;
  };
  outstanding_inr: number;
  entries: LedgerEntry[];
}

function fmtInr(rupees: number): string {
  return `₹${Math.abs(rupees).toLocaleString('en-IN')}`;
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function ResidentLedgerPage(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id: string };

  const query = useQuery({
    queryKey: ['resident-ledger', id],
    queryFn: () => get<LedgerResp>(`/residents/${id}/ledger`)
  });

  const data = query.data;
  const unitLabel = data?.resident.unit
    ? `${data.resident.unit.block?.name ?? '—'} / ${data.resident.unit.number}`
    : '—';

  return (
    <>
      <PageHeader
        title={data ? `Ledger — ${data.resident.full_name}` : 'Resident ledger'}
        description={data ? `${unitLabel} · ${data.resident.phone}` : 'Bills + payments running balance'}
      />
      <div className="mx-auto w-full max-w-[1200px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {query.isLoading ? (
          <Card>
            <CardContent className="p-vt-4">
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
        ) : query.isError ? (
          <Card>
            <CardContent className="p-vt-4">
              <EmptyState
                icon={<AlertTriangle className="h-6 w-6" />}
                title="Could not load ledger"
                description={getErrorMessage(query.error)}
                action={
                  <Button variant="secondary" onClick={() => void query.refetch()}>
                    Try again
                  </Button>
                }
              />
            </CardContent>
          </Card>
        ) : data ? (
          <>
            <Card>
              <CardContent className="flex items-center gap-vt-4 p-vt-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">
                  <Receipt className="h-6 w-6" />
                </span>
                <div>
                  <div className="text-vt-12 text-vt-text-muted">Outstanding balance</div>
                  <div className="text-vt-32 font-semibold text-vt-text">{fmtInr(data.outstanding_inr)}</div>
                  <div className="text-vt-12 text-vt-text-muted">{data.entries.length} entries on file</div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-vt-4">
                {data.entries.length === 0 ? (
                  <EmptyState
                    icon={<Receipt className="h-6 w-6" />}
                    title="No ledger entries"
                    description="No bills or payments recorded for this unit yet."
                  />
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Debit</TableHead>
                        <TableHead className="text-right">Credit</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.entries.map((e) => (
                        <TableRow key={`${e.kind}-${e.ref_id}-${e.date}`}>
                          <TableCell className="whitespace-nowrap text-vt-text-muted">
                            {fmtDateTime(e.date)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-vt-2">
                              {e.kind === 'bill' ? (
                                <Badge variant="info">
                                  <ArrowUpRight className="mr-1 h-3 w-3" /> Bill
                                </Badge>
                              ) : (
                                <Badge variant="ok">
                                  <ArrowDownRight className="mr-1 h-3 w-3" /> Payment
                                </Badge>
                              )}
                              <span className="text-vt-13 text-vt-text">{e.description}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono text-vt-13">
                            {e.debit_inr > 0 ? fmtInr(e.debit_inr) : '—'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-vt-13 text-[color:var(--vt-safe)]">
                            {e.credit_inr > 0 ? fmtInr(e.credit_inr) : '—'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-vt-13 font-semibold text-vt-text">
                            {fmtInr(e.balance_inr)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </>
        ) : null}
      </div>
    </>
  );
}
